import * as React from "react";

import logger from "../../log";
import fetchApi, { pairedFetchApi, logFetchError } from "../../util/fetchApi";

import { useStatus } from "./status";

export type FeatureName = string;

export const TrainerFeatureContext = React.createContext<FeatureName[]>([]);

export const TrainerFeatureProvider = React.memo(
    ({ children }: React.PropsWithChildren) => {
        const [fetching, setFetching] = React.useState(false);
        const [features, setFeatures] = React.useState<FeatureName[]>([]);
        const [lastFetchedClientId, setLastFetchedClientId] = React.useState<
            number | undefined
        >(undefined);

        const { status } = useStatus();

        React.useEffect(() => {
            async function checkRequiredFeatures() {
                if (status?.clientId) {
                    const result = await pairedFetchApi<string[]>(
                        status?.clientId,
                        "/api/features",
                    );
                    logger.info(
                        `Available trainer features: ${result.join(",")}`,
                    );
                    setFeatures(result);
                    setLastFetchedClientId(status.clientId);
                }
            }

            // Fetch features if we have a clientId and either:
            // 1. We haven't fetched for any clientId yet, OR
            // 2. The clientId has changed since last fetch
            if (
                status?.clientId &&
                status.clientId !== lastFetchedClientId &&
                !fetching
            ) {
                setFetching(true);
                checkRequiredFeatures()
                    .catch((e) => {
                        logFetchError(e);
                    })
                    .finally(() => setFetching(false));
            } else if (
                status?.clientId === undefined &&
                lastFetchedClientId !== undefined
            ) {
                logger.info("Clearing cached trainer features.");
                setFeatures([]);
                setLastFetchedClientId(undefined);
            }
        }, [status?.clientId, lastFetchedClientId, fetching]);

        return (
            <TrainerFeatureContext.Provider value={features}>
                {children}
            </TrainerFeatureContext.Provider>
        );
    },
);

TrainerFeatureProvider.displayName = "TrainerFeatureProvider";

export function useTrainerFeatures(): FeatureName[] {
    return React.useContext(TrainerFeatureContext);
}

export function useTrainerFeaturesAdmin(clientId: number): FeatureName[] {
    const [features, setFeatures] = React.useState<FeatureName[]>([]);

    React.useEffect(() => {
        async function checkRequiredFeatures() {
            if (clientId) {
                const result = await fetchApi<string[]>(
                    `/trainer/${clientId}/api/features`,
                );
                logger.info(`Available trainer features: ${result.join(",")}`);
                setFeatures(result);
            }
        }

        checkRequiredFeatures().catch((e) => {
            logFetchError(e);
        });
    }, [clientId]);

    return features;
}
